import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, CheckCircle2, ShieldCheck, Clock, Sparkles, X } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
const FULL_BUNDLE_URL = 'https://rutinasimple.com//cart/52235919327513:1';
const BASE_ONLY_URL = 'https://rutinasimple.com//cart/52219237794073:1';
const TRUE_TOTAL = 32.97;
const BUNDLE_PRICE = 26.68;
const DISCOUNT_PERCENT = 30;
const included = [{
  label: 'El Protocolo de la Piel de Lujo (Ebook 60 págs.)',
  highlight: true
}, {
  label: 'La Matriz de Dupes 2026',
  highlight: false
}, {
  label: 'Directorio Mundial de Farmacias',
  highlight: false
}, {
  label: 'Cuello y Manos de Porcelana',
  highlight: false
}, {
  label: 'Guía de Skincare para el Gym',
  highlight: false
}, {
  label: 'Mapa de pH de Limpiadores',
  highlight: false
}, {
  label: 'La Biblia de las Mezclas — Seguridad total con tus activos',
  highlight: true
}, {
  label: 'Skin-Tracker Pro — Calendario de 28 días + fotos de progreso',
  highlight: true
}];
export function UpsellPopup({
  isOpen,
  onClose
}: UpsellPopupProps) {
  const handleAccept = () => {
    trackAddToCart();
    window.open(FULL_BUNDLE_URL, '_blank');
    onClose();
  };
  const handleDecline = () => {
    trackAddToCart();
    window.open(BASE_ONLY_URL, '_blank');
    onClose();
  };
  return <AnimatePresence data-id="element-236">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-id="element-237">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDecline} data-id="element-238" />

          {/* Popup */}
          <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 30
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto z-10" data-id="element-239">
            {/* Close button */}
            <button onClick={handleDecline} className="absolute top-4 right-4 text-charcoal/40 hover:text-charcoal transition-colors z-20" aria-label="Cerrar" data-id="element-240">
              <X className="w-6 h-6" data-id="element-241" />
            </button>

            {/* Header Banner */}
            <div className="bg-charcoal text-cream px-6 py-5 rounded-t-3xl text-center relative overflow-hidden" data-id="element-242">
              <div className="absolute inset-0 bg-gold/5" data-id="element-243"></div>
              <div className="relative z-10" data-id="element-244">
                <span className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 animate-pulse" data-id="element-245">
                  Oferta exclusiva — Solo en esta pantalla
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight" data-id="element-246">
                  Espera... No te vayas sin esto
                </h2>
                <p className="text-cream/70 mt-2 text-sm" data-id="element-247">
                  ¿Segura que quieres dejar pasar la protección completa para tu
                  piel?
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-5" data-id="element-248">
              {/* Flashing discount badge */}
              <div className="flex justify-center" data-id="element-249">
                <span className="inline-flex items-center gap-2 bg-gold/10 border-2 border-gold text-charcoal font-bold text-lg px-5 py-2 rounded-full upsell-flash" data-id="element-250">
                  <Sparkles className="w-5 h-5 text-gold" data-id="element-251" />
                  {DISCOUNT_PERCENT}% DE DESCUENTO EN LOS EXTRAS
                  <Sparkles className="w-5 h-5 text-gold" data-id="element-252" />
                </span>
              </div>

              {/* Main copy */}
              <p className="text-center text-charcoal/80 leading-relaxed" data-id="element-253">
                El Protocolo te da la ciencia, pero{' '}
                <strong className="text-charcoal" data-id="element-254">
                  La Biblia de las Mezclas
                </strong>{' '}
                te protege de errores que inflaman tu piel, y el{' '}
                <strong className="text-charcoal" data-id="element-255">Skin-Tracker Pro</strong>{' '}
                asegura que no pierdas ni una sola noche de tu ciclo de 28 días.
              </p>
              <p className="text-center text-charcoal/60 text-sm" data-id="element-256">
                Llévate el sistema completo: ebook + 5 bonos + ambos extras de
                protección y seguimiento.
              </p>

              {/* What's included */}
              <div className="bg-cream rounded-2xl p-5 space-y-2" data-id="element-257">
                <h4 className="font-bold text-sm uppercase tracking-wider text-charcoal/50 mb-3" data-id="element-258">
                  Tu paquete completo incluye:
                </h4>
                {included.map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-259">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${item.highlight ? 'text-gold' : 'text-sage'}`} data-id="element-260" />
                    <span className={`text-sm ${item.highlight ? 'font-bold text-charcoal' : 'text-charcoal/70'}`} data-id="element-261">
                      {item.label}
                    </span>
                  </div>)}
              </div>

              {/* Pricing block */}
              <div className="text-center space-y-2 py-4 border-y border-charcoal/10" data-id="element-262">
                <div className="flex items-center justify-center gap-3" data-id="element-263">
                  <span className="text-2xl text-charcoal/40 line-through font-serif" data-id="element-264">
                    ${TRUE_TOTAL.toFixed(2)}
                  </span>
                  <span className="text-4xl md:text-5xl font-bold text-gold font-serif" data-id="element-265">
                    ${BUNDLE_PRICE.toFixed(2)}
                  </span>
                  <span className="text-charcoal/60 text-sm" data-id="element-266">USD</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-xs text-charcoal/50" data-id="element-267">
                  <Lock className="w-3 h-3" data-id="element-268" />
                  <span data-id="element-269">Precio especial disponible solo en esta pantalla</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3" data-id="element-270">
                <button onClick={handleAccept} className="w-full bg-gold text-charcoal font-bold py-4 px-6 rounded-xl text-base md:text-lg uppercase tracking-wide transition-all duration-300 upsell-glow hover:bg-gold-hover shadow-lg" data-id="element-271">
                  SÍ, QUIERO EL PAQUETE COMPLETO POR ${BUNDLE_PRICE.toFixed(2)}
                </button>

                <button onClick={handleDecline} className="w-full text-charcoal/40 hover:text-charcoal/60 text-sm py-2 transition-colors underline underline-offset-2" data-id="element-272">
                  No gracias, continuar sin los extras
                </button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 text-xs text-charcoal/40 pt-2" data-id="element-273">
                <div className="flex items-center gap-1" data-id="element-274">
                  <ShieldCheck className="w-4 h-4" data-id="element-275" />
                  <span data-id="element-276">Garantía de 7 días</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-277">
                  <Lock className="w-4 h-4" data-id="element-278" />
                  <span data-id="element-279">Pago 100% seguro</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-280">
                  <Clock className="w-4 h-4" data-id="element-281" />
                  <span data-id="element-282">Acceso inmediato</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}